import { useState, useEffect } from "react";
import { useRouter } from "next/router";

/**
 * The usePagination hook pulls the `after` and `before` query params from the current url.
 * When paginating forwards, we send `after` and `first` to the api.
 * When paginating backwards, we send `before` and `last` to the api.
 *
 * This follows the Relay cursor connections specification: https://relay.dev/graphql/connections.htm
 */
const usePagination = () => {
  // get the url query params from Next's router
  const { query } = useRouter();

  // pull the after and before cursors from the query
  const after = query.after as string | undefined;
  const before = query.before as string | undefined;

  const [{ afterCursor, beforeCursor, first, last }, setCursor] = useState<{
    afterCursor?: string;
    beforeCursor?: string;
    first?: number;
    last?: number;
  }>({
    afterCursor: after,
    beforeCursor: before,
    // if there are no initial query params, set the initial state of the `first` variable to 20 river items
    // if there's an `after` param, set `first` to 20
    // otherwise, `first` should be undefined
    first: !after && !before ? 20 : !!after ? 20 : undefined,
    // if there's a `before` query param, set `last` to 20
    last: before ? 20 : undefined,
  });

  useEffect(() => {
    // if the `after` query param exists, update the pagination state
    if (after) {
      setCursor({
        afterCursor: after,
        first: 20,
      });
    }

    // if the `before` query param exists, update the pagination state
    if (before) {
      setCursor({ beforeCursor: before, last: 20 });
    }
  }, [after, before]);

  return {
    after: afterCursor,
    before: beforeCursor,
    first,
    last,
  };
};

export default usePagination;
